import React, { useState } from "react"

import { Menu as MenuIcon, NavigateNext, MenuOpen } from '@mui/icons-material'
import {
  Box,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Breadcrumbs,
  useMediaQuery,
  useTheme,
  Tooltip,
} from '@mui/material'
import { Link } from "react-router-dom"

import { AppSidebar } from "../components/app-sidebar"
import { GlobalSearch } from "../components/global-search"
import { useUser } from "../hooks/use-user"
import { useBreadcrumbs } from "../lib/breadcrumbs"

const drawerWidth = 240

export default function Page({ children }: React.PropsWithChildren<object>) {
  const [mobileOpen, setMobileOpen] = useState(false)
  const [desktopOpen, setDesktopOpen] = useState(true)
  const { user } = useUser()
  const breadcrumbs = useBreadcrumbs()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen)
  }

  const handleDesktopDrawerToggle = () => {
    setDesktopOpen(!desktopOpen)
  }

  return (
    <Box sx={{ display: 'flex' }}>
      {/* Mobile sidebar */}
      {isMobile && (
        <AppSidebar
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          user={user}
        />
      )}

      {/* Desktop sidebar */}
      {!isMobile && desktopOpen && (
        <AppSidebar variant="permanent" user={user} open={desktopOpen} />
      )}

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: desktopOpen ? `calc(100% - ${drawerWidth}px)` : '100%' },
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        {/* App bar */}
        <AppBar
          position="fixed"
          sx={{
            width: { md: desktopOpen ? `calc(100% - ${drawerWidth}px)` : '100%' },
            ml: { md: desktopOpen ? `${drawerWidth}px` : 0 },
            bgcolor: 'rgba(250, 248, 242, 0.8)',
            color: 'text.primary',
            boxShadow: 'none',
            backdropFilter: 'blur(12px)',
            borderBottom: '1px solid',
            borderColor: 'divider',
            transition: theme.transitions.create(['width', 'margin'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
          }}
        >
          <Toolbar>
            {isMobile && (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ mr: 2 }}
              >
                <MenuIcon />
              </IconButton>
            )}

            {!isMobile && (
              <Tooltip title={desktopOpen ? "Hide sidebar" : "Show sidebar"}>
                <IconButton
                  color="inherit"
                  aria-label="toggle sidebar"
                  edge="start"
                  onClick={handleDesktopDrawerToggle}
                  sx={{ mr: 2 }}
                >
                  {desktopOpen ? <MenuOpen /> : <MenuIcon />}
                </IconButton>
              </Tooltip>
            )}

            {/* Breadcrumbs */}
            <Breadcrumbs
              separator={<NavigateNext fontSize="small" />}
              aria-label="breadcrumb"
              sx={{ display: { xs: 'none', md: 'flex' } }}
            >
              {breadcrumbs.map((breadcrumb, index) => (
                <React.Fragment key={index}>
                  {breadcrumb.isCurrentPage ? (
                    <Typography color="text.primary" variant="body2">
                      {breadcrumb.label}
                    </Typography>
                  ) : (
                    <Typography
                      component={Link}
                      to={breadcrumb.href || "#"}
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                        textDecoration: 'none',
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      {breadcrumb.label}
                    </Typography>
                  )}
                </React.Fragment>
              ))}
            </Breadcrumbs>

            <Box sx={{ ml: 'auto' }}>
              <GlobalSearch />
            </Box>
          </Toolbar>
        </AppBar>

        {/* Content area */}
        <Box
          sx={{
            mt: 8, // AppBar height
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  )
}
